import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { inside, mapDevices, type Placed } from "../map/geo";
import type { DeviceMap } from "../map/engine";
import { useMapStyle } from "../map/style";
import { Frame, Note, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";
import { VIRTUAL_ABOVE, VirtualList } from "./VirtualList";

const ROW = 28;

/**
 * map (WEB-F-DEV-05): the widget's devices on the platform's base map, clustered at zoom-out, moving as their
 * positions come in. The list under the map names every placed device (offline ones marked) and flies to it; it is
 * also the map's text alternative, virtualised above 100 rows. A marker click, or a row's details button, opens the
 * device's sheet where the page offers one.
 */
export function MapWidget({ widget, input, onOpenDevice, onVisibleDevices, fullPage = false, mapScreen }: WidgetProps) {
  const fmt = useWidgetText();
  const { t } = useTranslation();
  const { all, placed } = useMemo(() => mapDevices(input), [input]);
  const offline = all.some((id) => input.devices?.[id]?.state === "offline");
  const state = placed.length === 0 ? "empty" : offline ? "offline" : "normal";
  const [selected, setSelected] = useState<string | undefined>();
  // The region's street map (map-tiles MAP-10); none outside a shell, and none while regions.json loads.
  const mapStyle = useMapStyle();
  const style = mapStyle?.kind === "style" ? mapStyle.url : undefined;
  const noStreets = mapStyle?.kind === "none";
  // The Map screen's Satellite (maps-2 MAP2-01) is drawn on the same map; the street map stays its first style.
  const shown = mapScreen?.style ?? style;
  const shownRef = useRef(shown);
  const screenRef = useRef(mapScreen);

  const host = useRef<HTMLDivElement>(null);
  const mapRef = useRef<DeviceMap | undefined>(undefined);
  const placedRef = useRef(placed);
  const openRef = useRef(onOpenDevice);
  const visibleRef = useRef(onVisibleDevices);
  const gesturesRef = useRef({
    windows: t("widgets.map.zoomWindows"),
    mac: t("widgets.map.zoomMac"),
    mobile: t("widgets.map.zoomMobile"),
  });
  const viewRef = useRef<[[number, number], [number, number]] | undefined>(undefined);
  useEffect(() => {
    placedRef.current = placed;
    mapRef.current?.update(placed);
    if (viewRef.current) visibleRef.current?.(inside(placed, viewRef.current));
  }, [placed]);
  useEffect(() => {
    openRef.current = onOpenDevice;
    visibleRef.current = onVisibleDevices;
    screenRef.current = mapScreen;
  });

  // Drawn once the host has a size, with MapLibre imported only then. jsdom has no ResizeObserver (and no WebGL),
  // so the fixture tests check the list; a browser without WebGL keeps the list too.
  const drawable = (placed.length > 0 || !!mapScreen?.start) && mapStyle?.kind !== "pending";
  useEffect(() => {
    const el = host.current;
    if (!el || !drawable || typeof ResizeObserver === "undefined") return;
    let cancelled = false;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === 0) return;
      if (mapRef.current) {
        mapRef.current.resize();
        return;
      }
      void import("../map/engine")
        .then(({ createMap }) => {
          if (cancelled || mapRef.current) return;
          shownRef.current = screenRef.current?.style ?? style;
          mapRef.current = createMap(
            el,
            shownRef.current,
            placedRef.current,
            (id) => {
              setSelected(id);
              openRef.current?.(id);
            },
            (view) => {
              viewRef.current = view;
              visibleRef.current?.(inside(placedRef.current, view));
            },
            fullPage ? undefined : gesturesRef.current,
            screenRef.current && {
              start: screenRef.current.start,
              locate: screenRef.current.locate,
              onPlace: (lon, lat) => screenRef.current?.onPlace?.(lon, lat),
            },
          );
          const m = mapRef.current;
          m.setLabels(screenRef.current?.labels ?? true);
          m.setTrack(screenRef.current?.track);
          m.setPlacing(!!screenRef.current?.placing);
        })
        .catch(() => undefined);
    });
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
      mapRef.current?.remove();
      mapRef.current = undefined;
    };
  }, [drawable, style, fullPage]);

  useEffect(() => {
    if (!mapRef.current || shownRef.current === shown) return;
    shownRef.current = shown;
    mapRef.current.setStyle(shown);
  }, [shown]);
  const labels = mapScreen?.labels ?? true;
  const track = mapScreen?.track;
  const placing = !!mapScreen?.placing;
  useEffect(() => mapRef.current?.setLabels(labels), [labels]);
  useEffect(() => mapRef.current?.setTrack(track), [track]);
  useEffect(() => mapRef.current?.setPlacing(placing), [placing]);

  const flyTo = (d: Placed) => {
    setSelected(d.id);
    mapRef.current?.flyTo(d);
  };

  const row = (d: Placed) => (
    <>
      <button
        type="button"
        aria-current={selected === d.id ? "true" : undefined}
        onClick={() => flyTo(d)}
        className="flex min-w-0 items-center gap-1.5 rounded text-left text-gray-900 hover:underline aria-[current=true]:font-medium"
      >
        <span
          aria-hidden="true"
          className={`size-2 shrink-0 rounded-full ${d.state === "offline" ? "bg-red-600" : d.state === "sleeping" ? "bg-gray-400" : "bg-brand"}`}
        />
        <span className="truncate">{d.name}</span>
        {d.state === "offline" && <span className="text-xs text-red-800">{fmt.deviceState("offline")}</span>}
      </button>
      {onOpenDevice && (
        <button
          type="button"
          onClick={() => onOpenDevice(d.id)}
          aria-label={t("widgets.map.details", { name: d.name })}
          className="rounded px-1 text-xs text-brand hover:underline"
        >
          ⓘ
        </button>
      )}
    </>
  );

  return (
    <Frame widget={widget} state={state} title={widget.title}>
      {placed.length === 0 && !mapScreen?.start ? (
        <Note>{fmt.t("map.noPositions")}</Note>
      ) : (
        <>
          <div
            ref={host}
            role="region"
            aria-label={t("widgets.map.label", { count: placed.length })}
            className="min-h-48 flex-1 overflow-hidden rounded bg-[#eef2ee]"
          />
          {noStreets && <p className="text-xs text-gray-600">{t("widgets.map.noStreets")}</p>}
          {placed.length > VIRTUAL_ABOVE ? (
            <VirtualList
              items={placed}
              rowKey={(d) => d.id}
              row={row}
              rowHeight={ROW}
              label={t("widgets.map.devices")}
              active={placed.findIndex((d) => d.id === selected)}
            />
          ) : (
            <ul aria-label={t("widgets.map.devices")} className="flex flex-wrap gap-x-3 gap-y-1">
              {placed.map((d) => (
                <li key={d.id} className="flex items-center gap-1">
                  {row(d)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Frame>
  );
}
